import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

import ShiftsDataGrid from './ShiftsDataGrid';
import MINUTES_PER_DAY from '../../utilities/constants';
import SchedulingUtilities from '../../utilities/schedulingUtilities';
import { TipShareShift } from '../../types';
import {
  createShiftButtonSx,
  getFullDayCoverageWarningSx,
  paperSx,
  sectionTitleSx,
  shiftsSectionHeaderContentSx,
  shiftsSectionHeaderSx,
  shiftsSectionSx,
} from '../styles';

const doShiftsCoverAll24Hours = (shiftsData: TipShareShift[] | undefined): boolean => {
  if (!shiftsData?.length) return false;

  const coveredMinutes = new Array<boolean>(MINUTES_PER_DAY).fill(false);

  const markedAllDay = shiftsData.some((shift) => {
    const startMinutes = SchedulingUtilities.timeToMinutes(shift.startTime);
    const endMinutes = SchedulingUtilities.timeToMinutes(shift.endTime);

    if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
      return false;
    }

    const shiftCoveredMinutes = SchedulingUtilities.getCoveredMinutes(shift.startTime, shift.endTime);

    if (shiftCoveredMinutes.size === MINUTES_PER_DAY) {
      coveredMinutes.fill(true);
      return true;
    }

    shiftCoveredMinutes.forEach((minute) => {
      coveredMinutes[minute] = true;
    });

    return false;
  });

  if (
    !markedAllDay &&
    shiftsData.some(
      (shift) =>
        Number.isNaN(SchedulingUtilities.timeToMinutes(shift.startTime)) ||
        Number.isNaN(SchedulingUtilities.timeToMinutes(shift.endTime))
    )
  ) {
    return false;
  }

  return coveredMinutes.every(Boolean);
};

export interface TipShareShiftsSectionProps {
  isAutomaticDistribution: boolean;
  shiftsData: TipShareShift[] | undefined;
  isLoading: boolean;
  isEditor: boolean;
  onCreateClick: () => void;
  onRowClick: (shift: TipShareShift) => void;
  onEditClick: (shift: TipShareShift) => void;
  onDeleteClick: (shift: TipShareShift) => void;
}

const TipShareShiftsSection = memo(
  ({
    isAutomaticDistribution,
    shiftsData,
    isLoading,
    isEditor,
    onCreateClick,
    onRowClick,
    onEditClick,
    onDeleteClick,
  }: TipShareShiftsSectionProps) => {
    const { t } = useTranslation();
    const showShiftsRequiredWarning = isAutomaticDistribution && shiftsData?.length === 0;
    const showFullDayCoverageWarning = Boolean(shiftsData?.length) && !doShiftsCoverAll24Hours(shiftsData);

    return (
      <Paper elevation={0} sx={paperSx} data-testid='tip-share-shifts-section'>
        <Box sx={shiftsSectionSx}>
          <Box sx={shiftsSectionHeaderSx}>
            <Box sx={shiftsSectionHeaderContentSx}>
              <Typography variant='subtitle1' sx={sectionTitleSx} fontWeight={600}>
                {t('labor.tipShareShifts.pageTitle')}
              </Typography>

              <Typography
                variant='body2'
                data-testid='shifts-full-day-warning'
                sx={getFullDayCoverageWarningSx(showFullDayCoverageWarning)}
              >
                {showFullDayCoverageWarning ? t('labor.tipShareShifts.fullDayCoverageWarning') : ' '}
              </Typography>
            </Box>

            {isEditor && (
              <Button
                type='button'
                variant='contained'
                color='primary'
                onClick={onCreateClick}
                data-testid='create-shift-button'
                sx={createShiftButtonSx}
              >
                {t('labor.tipShareShifts.createButton')}
              </Button>
            )}
          </Box>
        </Box>

        {showShiftsRequiredWarning && (
          <Alert severity='warning' sx={{ mt: 1, mb: 2 }} data-testid='shifts-required-warning'>
            {t('labor.tipShare.validation.shiftsRequired')}
          </Alert>
        )}

        <ShiftsDataGrid
          loading={isLoading}
          data={shiftsData}
          isEditor={isEditor}
          highlightIncompleteCoverageEndTime={showFullDayCoverageWarning}
          onRowClick={onRowClick}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
        />
      </Paper>
    );
  }
);

export default TipShareShiftsSection;
